import type { VehicleType } from '@/types/models/vehicleGroup';
import { VEHICLE_TYPE } from '@/types/models/vehicleGroup';

export const BRAND_MODEL_OPTIONS_BY_TYPE = {
  [VEHICLE_TYPE.CAR]: {
    Toyota: ['Yaris', 'Corolla', 'Aygo', 'RAV4', 'C-HR'],
    Volkswagen: ['Polo', 'Golf', 'T-Roc', 'Tiguan', 'Passat'],
    BMW: ['116i', '218i', '320i', 'X1', 'X3'],
    'Mercedes-Benz': ['A-Class', 'C-Class', 'GLA', 'GLC', 'Vito'],
    Audi: ['A1', 'A3', 'A4', 'Q2', 'Q3'],
    Peugeot: ['108', '208', '2008', '3008', '508'],
    Fiat: ['500', 'Panda', 'Tipo', 'Doblo', '500X'],
    Hyundai: ['i10', 'i20', 'i30', 'Kona', 'Tucson'],
    Kia: ['Picanto', 'Rio', 'Ceed', 'Stonic', 'Sportage'],
    Nissan: ['Micra', 'Juke', 'Qashqai', 'X-Trail', 'Note'],
  },
  [VEHICLE_TYPE.MOTORCYCLE]: {
    Yamaha: ['MT-07', 'Tracer 7', 'XMAX 300', 'Tenere 700'],
    Honda: ['CB500F', 'NC750X', 'PCX 125', 'Africa Twin'],
    BMW: ['G 310 R', 'F 750 GS', 'F 900 XR', 'R 1250 GS'],
    Suzuki: ['V-Strom 650', 'GSX-8S', 'SV650', 'Burgman 400'],
    Kawasaki: ['Z650', 'Versys 650', 'Ninja 650', 'Z900'],
    Ducati: ['Monster', 'Multistrada V2', 'Scrambler Icon', 'Hypermotard 950'],
  },
  [VEHICLE_TYPE.ATV_QUAD]: {
    CFMOTO: ['CForce 450', 'CForce 520', 'CForce 625 Touring', 'CForce 1000'],
    Polaris: ['Sportsman 570', 'Sportsman XP 1000', 'Scrambler 850'],
    Yamaha: ['Kodiak 450', 'Kodiak 700', 'Grizzly 700'],
    Honda: ['FourTrax Rancher', 'FourTrax Foreman', 'TRX 520'],
    CanAm: ['Outlander 570', 'Outlander 700', 'Renegade 1000R'],
    Kymco: ['MXU 550', 'MXU 700i', 'Maxxer 300'],
  },
  [VEHICLE_TYPE.SCOOTER]: {
    Piaggio: ['Liberty 125', 'Beverly 300', 'Medley 150', 'MP3 300'],
    Vespa: ['Primavera 125', 'Sprint 150', 'GTS 300'],
    Yamaha: ['NMAX 125', 'XMAX 300', 'TMAX 560'],
    Honda: ['Vision 110', 'SH 125', 'Forza 350', 'ADV 350'],
    SYM: ['Symphony ST 125', 'Jet X 125', 'Cruisym 300'],
    Kymco: ['Agility 16+ 125', 'People S 200', 'X-Town 300'],
  },
} as const satisfies Record<VehicleType, Record<string, ReadonlyArray<string>>>;

const ALL_BRANDS = Object.values(BRAND_MODEL_OPTIONS_BY_TYPE).flatMap((brandsByType) =>
  Object.keys(brandsByType),
);

export const KNOWN_VEHICLE_BRANDS = [...new Set(ALL_BRANDS)].sort(
  (left, right) => right.length - left.length,
);

export const parseVehicleModelTitle = (title: string): { brand: string; modelName: string } => {
  const trimmedTitle = title.trim();
  const matchedBrand = KNOWN_VEHICLE_BRANDS.find(
    (brand) => trimmedTitle === brand || trimmedTitle.startsWith(`${brand} `),
  );

  if (!matchedBrand) {
    const [fallbackBrand, ...rest] = trimmedTitle.split(' ');
    return {
      brand: fallbackBrand ?? '',
      modelName: rest.join(' ').trim(),
    };
  }

  return {
    brand: matchedBrand,
    modelName: trimmedTitle.slice(matchedBrand.length).trim(),
  };
};

type VehicleModelNameSource = {
  brand?: string | null;
  model?: string | null;
  title?: string | null;
};

export const getVehicleModelTitle = ({ brand, model, title }: VehicleModelNameSource): string => {
  const trimmedTitle = title?.trim();
  if (trimmedTitle) {
    return trimmedTitle;
  }

  return [brand?.trim(), model?.trim()].filter(Boolean).join(' ');
};

export const getVehicleModelNameParts = ({
  brand,
  model,
  title,
}: VehicleModelNameSource): { brand: string; modelName: string } => {
  const trimmedBrand = brand?.trim() ?? '';
  const trimmedModel = model?.trim() ?? '';

  if (trimmedBrand || trimmedModel) {
    return {
      brand: trimmedBrand,
      modelName: trimmedModel,
    };
  }

  return parseVehicleModelTitle(title ?? '');
};
